import React, { useCallback, useEffect, useState } from 'react';
import './CycleCountReport.scss';
import { Button, Form, Table } from 'react-bootstrap';
import { ReactSVG } from 'react-svg';
import exporticon from '../../../assets/images/export-icon.svg';
import DatePicker from 'react-date-picker';
import Select from 'react-select';
import { FormikHelpers, useFormik } from 'formik';
import moment from 'moment';
import * as Yup from 'yup';
import isEqual from 'lodash/isEqual';
import useLocations from '../../../Hooks/useLocations';
import Loading from '../../../components/LoadingPage/Loading';
import { DropDownValues } from '../../../utils/types';
import useReports from '../../../Hooks/useReports';

const initialFormValues = {
    locationId: '',
    verifiedBefore: moment(Date()).toDate(),
};

const formatDate = (date: any, isOnlyDate = false) => {
    if (!date) return "-";
    return moment(date).format(isOnlyDate ? "MM/DD/YYYY" : "MM/DD/YYYY hh:mm:ss A");
};

const CycleCountReport: React.FC = () => {
    const [cycleCountList, setCycleCountList] = useState<any[]>([]);
    const [hasSearched, setHasSearched] = useState<boolean>(false);
    const [locationOptions, setLocationOptions] = useState<DropDownValues[]>([]);

    const { getLocationsAPI, getLocationsLoading } = useLocations();
    const { getCycleCountReportAPI, cycleCountReportLoading, exportCycleCountReportAPI, exportCycleCountReportLoading, exportCycleCountReportAsTallyAPI, exportCycleCountReportAsTallyLoading } = useReports();

    useEffect(() => {
        fetchLocations();
    }, []);

    const fetchLocations = async () => {
        try {
            const res = await getLocationsAPI({ active: true, sortColumn: 'locationName' });
            const options = res?.map((option: any) => ({
                value: option.id,
                label: `${option.locationName} ${option?.locationCode ? `(${option.locationCode})` : ''}`,
            }));
            setLocationOptions(options);
        } catch (error) {
            console.error('Error fetching locations:', error);
        }
    };

    const fetchCycleCountReport = async (filterValues: any) => {
        try {
            const params: Record<string, any> = {};
            if (filterValues.locationId) params.locationId = filterValues.locationId;
            if (filterValues.verifiedBefore) params.verifiedBefore = moment(filterValues.verifiedBefore).endOf('day').format('YYYY-MM-DDTHH:mm:ss');

            const res = await getCycleCountReportAPI(params);
            setCycleCountList(res || []);
            setHasSearched(true);
        } catch (error: any) {
            console.error("Error while fetching item verification list: ", error);
        }
    };

    const { handleChange, values, handleSubmit, resetForm, touched, errors } =
        useFormik({
            initialValues: initialFormValues,
            validationSchema: Yup.object({
                locationId: Yup.string().required("Location is required"),
            }),
            onSubmit: async (values: any, { setSubmitting }: FormikHelpers<any>) => {
                try {
                    await fetchCycleCountReport(values);
                } catch (err: any) { }
                setSubmitting(false);
            },
            enableReinitialize: true,
        });

    const isFilterChanged = () => !isEqual(values, initialFormValues);

    const handleClear = () => {
        if (!isFilterChanged()) return;
        resetForm();
        setCycleCountList([]);
        setHasSearched(false);
    };

    const downloadReport = useCallback(async (
        exportFn: (params: Record<string, any>) => Promise<any>,
        fileName: string,
    ) => {
        try {
            const params: Record<string, any> = {};
            if (values.locationId) params.locationId = values.locationId;
            if (values.verifiedBefore) params.verifiedBefore = moment(values.verifiedBefore).endOf('day').format('YYYY-MM-DDTHH:mm:ss');

            const res = await exportFn(params);
            const url = URL.createObjectURL(res);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `${localStorage.getItem("profileClientId")?.toUpperCase() ?? ""}_${fileName}_${moment().format("YYYYMMDD_HHmmss")}.xlsx`
            );
            document.body.appendChild(link);
            link.click();
        } catch (error: any) {
            console.error(`Error while exporting ${fileName}: `, error);
        }
    }, [values]);

    const handleExport = useCallback(() => {
        downloadReport(exportCycleCountReportAPI, "Cycle_Count_Report");
    }, [downloadReport]);

    const handleExportAsTally = useCallback(() => {
        downloadReport(exportCycleCountReportAsTallyAPI, "Cycle_Count_Tally_Sheet");
    }, [downloadReport]);

    const isLoading = getLocationsLoading || cycleCountReportLoading || exportCycleCountReportLoading || exportCycleCountReportAsTallyLoading;

    return (
        <div className='cycle-count-list-main'>
            {isLoading && (<Loading />)}
            <div className="cycle-count-list-main__header">
                <h2>Cycle Count Report</h2>
                {hasSearched && (<p>Displaying {cycleCountList.length || 0} of {cycleCountList.length || 0} Results</p>)}
            </div>
            <div className="report-filter-wrapper">
                <Form className='cycle-count-list-main__filter-form' onSubmit={handleSubmit}>
                    <Form.Group className='form-group location-field'>
                        <Form.Label htmlFor='location_code'>Location</Form.Label>
                        <Select
                            isSearchable={true}
                            classNamePrefix="react-select"
                            options={locationOptions}
                            id="location_code"
                            name="locationId"
                            className={`${touched.locationId && errors.locationId ? "is-invalid-border" : ""} react-select-container`}
                            value={locationOptions?.find((option: DropDownValues) => option.value === values.locationId) || null}
                            onChange={(option: any) => {
                                handleChange("locationId")(option?.value ?? '');
                            }}
                        />
                        {touched.locationId && errors.locationId && (<span className='error-msg'>{errors.locationId}</span>)}
                    </Form.Group>
                    <Form.Group className='form-group date-field'>
                        <Form.Label htmlFor='verified_before'>Verified Before</Form.Label>
                        <DatePicker
                            locale="en-US"
                            format="MM/dd/yyyy"
                            id="verified_before"
                            name='verifiedBefore'
                            value={values.verifiedBefore}
                            maxDate={new Date()}
                            openCalendarOnFocus={false}
                            showLeadingZeros={true}
                            dayPlaceholder="dd"
                            monthPlaceholder="mm"
                            yearPlaceholder="yyyy"
                            clearIcon={null}
                            calendarIcon={<i className="bi bi-calendar" />}
                            className="form-control"
                            onChange={(date: any) => {
                                handleChange({
                                    target: {
                                        name: "verifiedBefore",
                                        value: moment(date).isValid() ? moment(date).format("L") : "",
                                    },
                                });
                            }}
                        />
                    </Form.Group>
                    <Form.Group className='form-actions'>
                        <Button type='submit' variant='primary'>View</Button>
                        <Button type='button' variant='link' onClick={handleClear}>Clear</Button>
                    </Form.Group>
                </Form>
                <div className="report-export-box">
                    <button
                        type="button"
                        className="report-export-btn"
                        onClick={handleExport}
                    >
                        <ReactSVG className="svg-box" wrapper="span" src={exporticon} />
                        Export
                    </button>
                    <button
                        type="button"
                        className="report-export-btn"
                        onClick={handleExportAsTally}
                    >
                        <ReactSVG className="svg-box" wrapper="span" src={exporticon} />
                        Export as TallySheet
                    </button>
                </div>
            </div>

            {hasSearched && (
                <div className="cycle-count-list-main__table-wrapper">
                    <Table responsive hover={true}>
                        <thead>
                            <tr>
                                <th>Item Name</th>
                                <th>RxID</th>
                                <th>NDC</th>
                                <th>Lot No</th>
                                <th>Exp. Date</th>
                                <th className='text-end'>Quantity</th>
                                <th>Verified By</th>
                                <th>Verified Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cycleCountList?.length > 0 ? (
                                cycleCountList.map((cycleCountItem: any, index: number) => (
                                    <tr key={cycleCountItem?.id || index}>
                                        <td title={cycleCountItem?.item?.rxName || ""}>{cycleCountItem?.item?.rxName || "-"}</td>
                                        <td title={cycleCountItem?.item?.rxSRXId || ""}>{cycleCountItem?.item?.rxSRXId || "-"}</td>
                                        <td title={cycleCountItem?.item?.ndcCode || ""}>{cycleCountItem?.item?.ndcCode || "-"}</td>
                                        <td title={cycleCountItem?.item?.lotNo || ""}>{cycleCountItem?.item?.lotNo || "-"}</td>
                                        <td title={formatDate(cycleCountItem?.item?.expiryDate, true)}>{formatDate(cycleCountItem?.item?.expiryDate, true)}</td>
                                        <td className='text-end' title={cycleCountItem?.quantity?.toString() || ""}>{cycleCountItem?.quantity ?? "-"}</td>
                                        <td title={cycleCountItem?.user?.userName || ""}>{cycleCountItem?.user?.userName || "-"}</td>
                                        <td title={formatDate(cycleCountItem?.verifyDate)}>{formatDate(cycleCountItem?.verifyDate)}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={8} className="text-center">
                                        No records found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            )}
        </div>
    )
}

export default CycleCountReport;
